import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Loader2, AlertTriangle, Check, FileText, Clock, HardDrive } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { deleteFromR2 } from '../../config/storage';
import { formatFileSize } from '../../utils/file-types';
import { cn } from '../../utils/cn';
import ConfirmModal from '../shared/ConfirmModal';

interface DuplicateGroup {
  fileName: string;
  files: {
    id: string;
    name: string;
    size: number;
    storage_key: string;
    created_at: string;
    isKeep: boolean; // 标记保留的文件（最早的那个）
  }[];
}

interface DuplicateFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  onDeleted?: () => void; // 删除完成后的回调
}

const DuplicateFilesModal: React.FC<DuplicateFilesModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  onDeleted
}) => {
  const [loading, setLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number; currentFile?: string; startTime?: number } | null>(null);
  const [deleteResult, setDeleteResult] = useState<{ success: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState<{ loaded: number; phase: string } | null>(null);

  // 查找重复文件 - 使用分页获取所有文件
  const findDuplicates = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDuplicateGroups([]);
    setSelectedForDelete(new Set());
    setDeleteResult(null);
    setScanProgress({ loaded: 0, phase: '正在扫描...' });

    try {
      // 使用分页查询获取所有文件（每次最多 1000 条）
      const PAGE_SIZE = 1000;
      let allFiles: { id: string; name: string; size: number; storage_key: string; created_at: string }[] = [];
      let offset = 0;
      let hasMore = true;

      setScanProgress({ loaded: 0, phase: '正在从服务器加载文件列表...' });

      while (hasMore) {
        const { data, error: queryError } = await supabase
          .from('file_categories')
          .select(`
            file:files!inner(
              id,
              name,
              size,
              storage_key,
              created_at,
              deleted_at
            )
          `)
          .eq('category_id', categoryId)
          .is('file.deleted_at', null)
          .range(offset, offset + PAGE_SIZE - 1);

        if (queryError) throw queryError;

        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          // 提取文件数据
          for (const item of data) {
            if (item.file) {
              const file = item.file as any;
              allFiles.push({
                id: file.id,
                name: file.name,
                size: file.size || 0,
                storage_key: file.storage_key,
                created_at: file.created_at
              });
            }
          }
          
          offset += data.length;
          setScanProgress({ loaded: allFiles.length, phase: `已加载 ${allFiles.length} 个文件...` });

          // 如果返回的数据少于 PAGE_SIZE，说明没有更多数据了
          if (data.length < PAGE_SIZE) {
            hasMore = false;
          }
        }
      }

      console.log(`[DuplicateCheck] 共加载 ${allFiles.length} 个文件`);
      setScanProgress({ loaded: allFiles.length, phase: '正在分析重复文件...' });

      // 按文件名分组
      const filesByName = new Map<string, typeof allFiles>();
      
      for (const file of allFiles) {
        const name = file.name;
        if (!filesByName.has(name)) {
          filesByName.set(name, []);
        }
        filesByName.get(name)!.push(file);
      }

      // 找出重复的（超过1个的）
      const groups: DuplicateGroup[] = [];
      
      for (const [fileName, files] of filesByName.entries()) {
        if (files.length > 1) {
          // 按创建时间排序，最早的放前面
          const sortedFiles = files.sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          
          groups.push({
            fileName,
            files: sortedFiles.map((f, index) => ({
              ...f,
              isKeep: index === 0 // 第一个（最早的）标记为保留
            }))
          });
        }
      }

      // 按重复数量排序
      groups.sort((a, b) => b.files.length - a.files.length);

      console.log(`[DuplicateCheck] 发现 ${groups.length} 组重复文件`);
      setDuplicateGroups(groups);

      // 自动选中所有非保留的文件（即重复的副本）
      const toDelete = new Set<string>();
      for (const group of groups) {
        for (const file of group.files) {
          if (!file.isKeep) {
            toDelete.add(file.id);
          }
        }
      }
      setSelectedForDelete(toDelete);

    } catch (err: any) {
      console.error('查找重复文件失败:', err);
      setError(err.message || '查找重复文件时出错');
    } finally {
      setLoading(false);
      setScanProgress(null);
    }
  }, [categoryId]);

  // 打开时自动查找
  useEffect(() => {
    if (isOpen && categoryId) {
      findDuplicates();
    }
  }, [isOpen, categoryId, findDuplicates]);

  // 切换选择
  const toggleSelect = (fileId: string) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  // 全选所有重复文件（非保留的）
  const selectAllDuplicates = () => {
    const toDelete = new Set<string>();
    for (const group of duplicateGroups) {
      for (const file of group.files) {
        if (!file.isKeep) {
          toDelete.add(file.id);
        }
      }
    }
    setSelectedForDelete(toDelete);
  };

  // 取消全选
  const deselectAll = () => {
    setSelectedForDelete(new Set());
  };

  // 添加取消标志
  const cancelRef = React.useRef(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 批量删除 - 优化版：分批处理 + 可取消
  const handleBatchDelete = async () => {
    if (selectedForDelete.size === 0) return;
    setShowDeleteConfirm(true); // 3. 将 window.confirm 替换为内部弹窗
  };

  // 执行删除
  const executeDelete = async () => {
    setShowDeleteConfirm(false);

    setIsDeleting(true);
    cancelRef.current = false;
    setIsCancelling(false);
    const startTime = Date.now();
    setDeleteProgress({ current: 0, total: selectedForDelete.size, startTime });
    setDeleteResult(null);

    let success = 0;
    let failed = 0;
    const failedFiles: string[] = [];

    // 获取要删除的文件信息
    const filesToDelete: { id: string; storage_key: string; name: string }[] = [];
    for (const group of duplicateGroups) {
      for (const file of group.files) {
        if (selectedForDelete.has(file.id)) {
          filesToDelete.push({
            id: file.id,
            storage_key: file.storage_key,
            name: file.name
          });
        }
      }
    }

    // 单个文件删除函数
    const deleteOneFile = async (file: { id: string; storage_key: string; name: string }) => {
      try {
        // 1. 先删除 R2 存储
        if (file.storage_key) {
          try {
            await deleteFromR2(file.storage_key);
          } catch (r2Error) {
            console.warn(`R2 删除失败 (可能已不存在): ${file.storage_key}`, r2Error);
          }
        }

        // 2. 删除 file_categories 关联
        await supabase
          .from('file_categories')
          .delete()
          .eq('file_id', file.id)
          .eq('category_id', categoryId);

        // 3. 检查该文件是否还在其他分类中
        const { count } = await supabase
          .from('file_categories')
          .select('*', { count: 'exact', head: true })
          .eq('file_id', file.id);

        // 4. 如果没有其他关联，删除文件记录
        if (count === 0) {
          await supabase
            .from('files')
            .delete()
            .eq('id', file.id);
        }

        return true;
      } catch (err: any) {
        console.error(`删除文件失败: ${file.name}`, err);
        return false;
      }
    };

    // 分批处理，每批 50 个并行删除（拉满配置）
    // Cloudflare R2: 支持约 5000 RPS/bucket，无硬性限制
    // Supabase: 写操作限制约 100/5分钟/IP，但每个请求很快完成
    // 50 并发是稳定且高效的选择
    const BATCH_SIZE = 50;
    
    for (let i = 0; i < filesToDelete.length; i += BATCH_SIZE) {
      // 检查是否取消
      if (cancelRef.current) {
        console.log('[DuplicateDelete] 用户取消删除操作');
        break;
      }

      const batch = filesToDelete.slice(i, i + BATCH_SIZE);
      const currentFile = batch[0]?.name || '';
      
      setDeleteProgress({ 
        current: i + batch.length, 
        total: filesToDelete.length, 
        currentFile 
      });

      // 并行执行本批次的删除
      const results = await Promise.all(batch.map(deleteOneFile));
      
      for (let j = 0; j < results.length; j++) {
        if (results[j]) {
          success++;
        } else {
          failed++;
          failedFiles.push(batch[j].name);
        }
      }

      // 每批次之间短暂休息 50ms，避免服务器过载
      if (i + BATCH_SIZE < filesToDelete.length && !cancelRef.current) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    setDeleteProgress(null);
    setDeleteResult({ success, failed });
    setIsDeleting(false);
    setIsCancelling(false);

    // 如果有失败的文件，输出到控制台
    if (failedFiles.length > 0) {
      console.log('[DuplicateDelete] 删除失败的文件:', failedFiles);
    }

    // 刷新列表
    if (success > 0) {
      await findDuplicates();
      onDeleted?.();
    }
  };

  // 取消删除
  const handleCancelDelete = () => {
    cancelRef.current = true;
    setIsCancelling(true);
  };

  // 计算统计信息
  const stats = React.useMemo(() => {
    let totalDuplicates = 0;
    let totalSize = 0;
    
    for (const group of duplicateGroups) {
      for (const file of group.files) {
        if (!file.isKeep) {
          totalDuplicates++;
          totalSize += file.size || 0;
        }
      }
    }

    return { totalDuplicates, totalSize };
  }, [duplicateGroups]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
        {/* 头部 */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              检查重复文件
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              在「{categoryName}」中查找并清理重复文件
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-sky-500 animate-spin mb-4" />
              <p className="text-slate-700 font-medium">正在扫描重复文件...</p>
              {scanProgress && (
                <p className="text-slate-500 text-sm mt-2">{scanProgress.phase}</p>
              )}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-red-600 font-medium">扫描失败</p>
              <p className="text-slate-500 mt-2">{error}</p>
              <button
                onClick={findDuplicates}
                className="mt-4 px-4 py-2 text-sm font-medium text-sky-600 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors"
              >
                重新扫描
              </button>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Check className="w-12 h-12 text-green-500 mb-4" />
              <p className="text-lg font-medium text-slate-800">没有发现重复文件！</p>
              <p className="text-slate-500 mt-2">当前文件夹中的所有文件都是唯一的</p>
            </div>
          ) : (
            <>
              {/* 统计信息 */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-amber-600" />
                      <span className="text-amber-800">
                        发现 <strong>{duplicateGroups.length}</strong> 组重复文件
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HardDrive size={18} className="text-amber-600" />
                      <span className="text-amber-800">
                        可清理 <strong>{formatFileSize(stats.totalSize)}</strong>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllDuplicates}
                      className="px-3 py-1.5 text-sm font-medium text-sky-600 bg-white border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors"
                    >
                      全选重复项
                    </button>
                    <button
                      onClick={deselectAll}
                      className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      取消全选
                    </button>
                  </div>
                </div>
              </div>

              {/* 删除结果提示 */}
              {deleteResult && (
                <div className={cn(
                  "rounded-xl p-4 mb-6",
                  deleteResult.failed > 0 
                    ? "bg-amber-50 border border-amber-200" 
                    : "bg-green-50 border border-green-200"
                )}>
                  <p className={deleteResult.failed > 0 ? "text-amber-800" : "text-green-800"}>
                    删除完成：成功 <strong>{deleteResult.success}</strong> 个
                    {deleteResult.failed > 0 && <span>，失败 <strong>{deleteResult.failed}</strong> 个</span>}
                  </p>
                </div>
              )}

              {/* 重复文件列表 */}
              <div className="space-y-4">
                {duplicateGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">
                      {group.fileName}
                      <span className="ml-2 text-slate-400">({group.files.length} 个副本)</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {group.files.map((file, fileIndex) => (
                        <div
                          key={`${groupIndex}-${fileIndex}-${file.id}`}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3 transition-colors",
                            file.isKeep 
                              ? "bg-green-50" 
                              : selectedForDelete.has(file.id) 
                                ? "bg-red-50" 
                                : "hover:bg-slate-50"
                          )}
                        >
                          {/* 选择框 */}
                          {!file.isKeep && (
                            <input
                              type="checkbox"
                              checked={selectedForDelete.has(file.id)}
                              onChange={() => toggleSelect(file.id)}
                              className="w-4 h-4 accent-red-500 cursor-pointer"
                            />
                          )}
                          {file.isKeep && (
                            <div className="w-4 h-4 flex items-center justify-center">
                              <Check size={14} className="text-green-600" />
                            </div>
                          )}

                          {/* 文件信息 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {file.isKeep && (
                                <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded">
                                  保留
                                </span>
                              )}
                              {!file.isKeep && selectedForDelete.has(file.id) && (
                                <span className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded">
                                  将删除
                                </span>
                              )}
                            </div>
                          </div>

                          {/* 时间和大小 */}
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <div className="flex items-center gap-1">
                              <Clock size={14} />
                              <span>{new Date(file.created_at).toLocaleString('zh-CN')}</span>
                            </div>
                            <div className="w-20 text-right font-mono">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* 底部操作栏 */}
        {duplicateGroups.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              已选择 <strong className="text-red-600">{selectedForDelete.size}</strong> 个文件待删除
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                关闭
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={isDeleting || selectedForDelete.size === 0}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    删除中 ({deleteProgress?.current}/{deleteProgress?.total})
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    删除选中的 {selectedForDelete.size} 个文件
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* 删除进度条 */}
        {isDeleting && deleteProgress && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-100">
            <div className="flex items-center justify-between text-sm text-red-700 mb-2">
              <span className="truncate flex-1 mr-4">
                {isCancelling ? '正在取消...' : `正在删除: ${deleteProgress.currentFile}`}
              </span>
              <div className="flex items-center gap-3">
                <span className="font-mono">{deleteProgress.current} / {deleteProgress.total}</span>
                {/* 预估剩余时间 */}
                {deleteProgress.current > 0 && deleteProgress.startTime && (
                  <span className="text-xs text-red-500">
                    剩余约 {(() => {
                      const elapsed = Date.now() - deleteProgress.startTime;
                      const rate = deleteProgress.current / (elapsed / 1000); // 每秒处理数
                      const remaining = deleteProgress.total - deleteProgress.current;
                      const remainingSeconds = Math.ceil(remaining / rate);
                      if (remainingSeconds < 60) return `${remainingSeconds} 秒`;
                      if (remainingSeconds < 3600) return `${Math.ceil(remainingSeconds / 60)} 分钟`;
                      return `${Math.ceil(remainingSeconds / 3600)} 小时`;
                    })()}
                  </span>
                )}
                <button
                  onClick={handleCancelDelete}
                  disabled={isCancelling}
                  className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCancelling ? '取消中...' : '取消'}
                </button>
              </div>
            </div>
            <div className="h-2 bg-red-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-500 transition-all duration-300"
                style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={executeDelete}
        type="danger"
        title="确认删除重复文件"
        message={`确定要删除 ${selectedForDelete.size} 个重复文件吗？`}
        confirmText="确认删除"
        cancelText="取消"
      >
        <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-700 space-y-1">
          <p>• 预计耗时：约 {Math.max(1, Math.ceil(selectedForDelete.size / 500))} 分钟</p>
          <p>• 此操作将同时删除存储文件和数据库记录</p>
          <p>• 删除过程中可以点击"取消"按钮中断</p>
          <p className="font-medium">⚠️ 删除后无法恢复！</p>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default DuplicateFilesModal;
